import { useRef, useState } from 'react';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import {
  addFolderToEmptyFolders,
  createFile,
  createFolder,
  getCheckedFiles,
  getRootFolder,
  removeEmptyFolders,
  removeFileName,
  sortByType,
  updateBranchInTree,
  updateCheckedPaths,
} from 'src/utils/fileUtils';
import type { KBFile, TreeNode } from 'src/utils/fileUtils';
import { FILE_STATE } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';

interface UseKnowledgeBaseFileActionsParams {
  knowledgeBaseId: string | undefined;
  tree: TreeNode;
  activeBranch: TreeNode;
  updateActiveBranch: (branch: TreeNode) => void;
  skipRefetchFiles: boolean;
  setSkipRefetchFiles: (skip: boolean) => void;
  addKnowledgeBaseFile: (args: { id?: string; body: FormData }) => any;
  addKnowledgeBaseDocument: (args: { id?: string; body: unknown }) => any;
  deleteKnowledgeBaseFiles: (args: { id?: string; body: string[] }) => any;
  updateKnowledgeBaseFile: (args: {
    knowledgeBaseId?: string;
    fileId: string;
    body: unknown;
  }) => any;
  updateKnowledgeBaseFiles: (args: { id?: string; body: unknown[] }) => any;
  refetchFilesAndSync: () => Promise<any>;
  cancelCheckboxMode: () => void;
  resetCheckedItems: (treeArg?: TreeNode) => void;
}

export const useKnowledgeBaseFileActions = ({
  knowledgeBaseId,
  tree,
  activeBranch,
  updateActiveBranch,
  skipRefetchFiles,
  setSkipRefetchFiles,
  addKnowledgeBaseFile,
  addKnowledgeBaseDocument,
  deleteKnowledgeBaseFiles,
  updateKnowledgeBaseFile,
  updateKnowledgeBaseFiles,
  refetchFilesAndSync,
  cancelCheckboxMode,
  resetCheckedItems,
}: UseKnowledgeBaseFileActionsParams) => {
  const notification = useNotification();
  const loader = useLoader();
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [fileInputValue, setFileInputValue] = useState('');
  const [dragStarted, setDragStarted] = useState(false);

  const addFilesToBranch = (newFiles: KBFile[], branch: TreeNode) => {
    const newBranch = { ...branch };

    newFiles.forEach((file) => {
      const fileItem = createFile(file, newBranch);

      newBranch.children.push(fileItem);
    });

    sortByType(newBranch.children);
    updateBranchInTree(tree, branch, newBranch);

    if (branch === activeBranch) {
      updateActiveBranch(newBranch);
    }
  };

  const uploadFiles = (fileList: FileList): KBFile[] => {
    const newFiles: KBFile[] = [];

    if (!fileList.length) {
      return newFiles;
    }

    const promises: Promise<any>[] = [];

    loader.show({ progress: true });

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList.item(i) as File;
      const request = new FormData();
      const filePath = activeBranch.path || '';

      request.append('file', file, file.name);
      request.append('file_path', filePath);

      promises.push(addKnowledgeBaseFile({ id: knowledgeBaseId, body: request }));
      newFiles.push({ name: file.name, path: filePath, state: FILE_STATE.UPLOADING });
    }

    Promise.allSettled(promises)
      .then((results) => {
        const hasFailure = results.some((result) => result.status === 'rejected');

        if (hasFailure) {
          notification.error('Some files failed to upload.');
        }

        setFileInputValue('');

        if (!skipRefetchFiles) {
          return refetchFilesAndSync();
        }
      })
      .catch(() => {
        notification.error('Failed to upload files.');
      })
      .finally(() => {
        loader.hide();
      });

    return newFiles;
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const newFiles = uploadFiles(event.dataTransfer.files);

    addFilesToBranch(newFiles, activeBranch);
    setDragStarted(false);
  };

  const onDragEnter = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDragStarted(true);
  };

  const onDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
  };

  const onDragLeave = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDragStarted(false);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) {
      return;
    }

    const newFiles = uploadFiles(event.target.files);

    addFilesToBranch(newFiles, activeBranch);
  };

  const handleAddFolderItem = (folderName: string) => {
    const folder = createFolder(folderName, activeBranch);
    const newActiveBranch = { ...activeBranch };

    newActiveBranch.children.push(folder);
    updateBranchInTree(tree, activeBranch, newActiveBranch);
    updateActiveBranch(newActiveBranch);
  };

  const handleAddKnowledgeBaseDocument = async (data: { question: string; answer: string }) => {
    try {
      const response: any = await addKnowledgeBaseDocument({
        id: knowledgeBaseId,
        body: {
          name: data.question,
          text: data.answer,
          path: activeBranch.path,
        },
      });

      if (response?.error) {
        throw response.error;
      }

      if (!response?.data?.status) {
        addFilesToBranch([response.data], activeBranch);
      }
    } catch (error) {
      notification.error('Failed to add document.');
    }
  };

  const addKnowledgeBaseSource = (source: { type: string }, data?: any) => {
    switch (source.type) {
      case 'file':
        inputFileRef.current?.click();
        break;
      case 'faq':
        handleAddKnowledgeBaseDocument(data);
        break;
    }
  };

  const onAddKnowledgeBaseSourceOpen = (isOpen: boolean) => {
    setSkipRefetchFiles(isOpen);
  };

  const handleItemsDelete = async (filesArg?: TreeNode[]) => {
    const rootTree = getRootFolder(activeBranch);
    const filesToDelete = filesArg || getCheckedFiles(rootTree);
    const request = filesToDelete
      .filter((file) => !['folder', 'website', 'integration'].includes(file.type))
      .map((file) => file.id);

    if (filesToDelete.length && !request.length) {
      const newActiveBranch = { ...activeBranch };

      newActiveBranch.children = newActiveBranch.children.filter((child) => !child.checked);

      removeEmptyFolders(filesToDelete);
      updateActiveBranch(newActiveBranch);
      updateBranchInTree(rootTree, activeBranch, newActiveBranch);
      resetCheckedItems(rootTree);
      cancelCheckboxMode();
      return;
    }

    if (request.length) {
      loader.show();

      try {
        await deleteKnowledgeBaseFiles({ id: knowledgeBaseId, body: request }).unwrap();
      } catch (error) {
        notification.error('Failed to delete items.');
        return;
      } finally {
        loader.hide();
      }
    }

    cancelCheckboxMode();
  };

  const handleMoveItems = async (moveToFolder: TreeNode) => {
    const rootTree = getRootFolder(activeBranch);
    const checkedFiles = getCheckedFiles(rootTree);
    const filesWithUpdatedPaths = updateCheckedPaths(checkedFiles, moveToFolder.path);

    const request = filesWithUpdatedPaths
      .filter((file) => file.type !== 'folder')
      .map((file) => {
        const isFAQ = file.type === 'FAQ';

        return {
          id: file.id,
          path: removeFileName(file.newPath, file.name),
          name: isFAQ ? `${file.name}.faq` : file.name,
        };
      });

    filesWithUpdatedPaths.forEach((file) => addFolderToEmptyFolders(file));

    try {
      if (request.length === 1) {
        await updateKnowledgeBaseFile({
          knowledgeBaseId,
          fileId: request[0].id,
          body: { name: request[0].name, path: request[0].path },
        }).unwrap();
      } else if (request.length > 1) {
        await updateKnowledgeBaseFiles({ id: knowledgeBaseId, body: request }).unwrap();
      } else {
        const newActiveBranch = { ...activeBranch };

        newActiveBranch.children = newActiveBranch.children.filter((child) => !child.checked);
        updateBranchInTree(rootTree, activeBranch, newActiveBranch);
        updateActiveBranch(newActiveBranch);
        notification.success('Updated Successfully.');
      }
    } catch (error) {
      notification.error('Failed to move items.');
      return;
    }

    cancelCheckboxMode();
  };

  return {
    dragStarted,
    onDragEnter,
    onDragOver,
    onDragLeave,
    handleDrop,
    onFileChange,
    fileInputValue,
    inputFileRef,
    handleAddFolderItem,
    addKnowledgeBaseSource,
    onAddKnowledgeBaseSourceOpen,
    handleItemsDelete,
    handleMoveItems,
  };
};
